import React, { useState, useEffect } from 'react';
import { 
  Calendar, Palmtree, Briefcase, Heart, Baby, Clock,
  Plus, CheckCircle2, XCircle, AlertCircle, Info, TrendingUp
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatDate, getDaysBetween } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';

// Base leave types - maternity/paternity will be filtered by gender
const allLeaveTypes = [
  { value: 'annual', label: 'Annual Leave', icon: Palmtree, color: 'bg-blue-100 text-blue-600', description: 'Vacation and personal time off' },
  { value: 'sick', label: 'Sick Leave', icon: Heart, color: 'bg-red-100 text-red-600', description: 'Medical and health-related leave' },
  { value: 'casual', label: 'Casual Leave', icon: Briefcase, color: 'bg-amber-100 text-amber-600', description: 'Short notice personal leave' },
  { value: 'maternity', label: 'Maternity Leave', icon: Baby, color: 'bg-pink-100 text-pink-600', description: 'Maternity leave for expecting mothers', genderFilter: 'female' },
  { value: 'paternity', label: 'Paternity Leave', icon: Baby, color: 'bg-cyan-100 text-cyan-600', description: 'Paternity leave for new fathers', genderFilter: 'male' },
  { value: 'compensatory', label: 'Comp Off', icon: Clock, color: 'bg-purple-100 text-purple-600', description: 'Compensatory time off' }
];

// Helper to get leave types based on gender
const getLeaveTypesForUser = (gender) => {
  return allLeaveTypes.filter(lt => {
    if (!lt.genderFilter) return true; // No gender filter, show to all
    return lt.genderFilter === gender;
  });
};

export default function LeaveBalance() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [newRequest, setNewRequest] = useState({
    type: '',
    startDate: '',
    endDate: '',
    reason: ''
  });

  // Get leave types filtered by user's gender
  const userGender = user?.profile?.gender;
  const leaveTypes = getLeaveTypesForUser(userGender);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      // Get leave balance(s) for this user
      const balances = await db.leaveBalances.where('userId').equals(user.id).toArray();
      
      let balance = null;
      
      if (balances.length === 0) {
        // Create default leave balance with nested structure
        // Include both maternity and paternity - display will filter by gender
        const defaultBalance = {
          userId: user.id,
          year: new Date().getFullYear(),
          annual: { total: 21, used: 0, pending: 0 },
          sick: { total: 12, used: 0, pending: 0 },
          casual: { total: 6, used: 0, pending: 0 },
          maternity: { total: 180, used: 0, pending: 0 },
          paternity: { total: 15, used: 0, pending: 0 },
          compensatory: { total: 0, used: 0, pending: 0 }
        };
        await db.leaveBalances.add(defaultBalance);
        balance = defaultBalance;
      } else if (balances.length === 1 && balances[0].annual && typeof balances[0].annual === 'object') {
        // Single record with nested structure (new format)
        // Add paternity if not exists
        if (!balances[0].paternity) {
          balances[0].paternity = { total: 15, used: 0, pending: 0 };
        }
        balance = balances[0];
      } else if (balances.length >= 1 && balances[0].leaveType) {
        // Multiple records with flat structure (old format) - migrate to new format
        const aggregated = {
          userId: user.id,
          year: new Date().getFullYear(),
          annual: { total: 0, used: 0, pending: 0 },
          sick: { total: 0, used: 0, pending: 0 },
          casual: { total: 0, used: 0, pending: 0 },
          maternity: { total: 0, used: 0, pending: 0 },
          paternity: { total: 15, used: 0, pending: 0 },
          compensatory: { total: 0, used: 0, pending: 0 }
        };
        
        for (const b of balances) {
          const type = b.leaveType === 'personal' ? 'casual' : b.leaveType;
          if (aggregated[type]) {
            aggregated[type] = {
              total: b.totalQuota || b.total || 0,
              used: b.used || 0,
              pending: b.pending || 0
            };
          }
        }
        balance = aggregated;
      } else {
        // Fallback - use first record
        balance = balances[0];
      }
      
      setLeaveBalance(balance);
      
      // Load actual leave requests from database
      const requests = await db.leaveRequests
        .where('userId')
        .equals(user.id)
        .toArray();
      
      setLeaveRequests(requests.map(r => ({
        id: r.id,
        type: r.leaveType,
        startDate: new Date(r.startDate).toISOString().split('T')[0],
        endDate: new Date(r.endDate).toISOString().split('T')[0],
        days: r.days,
        reason: r.reason,
        status: r.status,
        rejectionReason: r.rejectionReason || r.ceoComments || null,
        ceoComments: r.ceoComments || null,
        reviewComments: r.reviewComments || null,
        submittedAt: r.createdAt
      })));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.type || !newRequest.startDate || !newRequest.endDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const days = getDaysBetween(newRequest.startDate, newRequest.endDate) + 1;
    const leaveType = newRequest.type;
    const rawBalance = leaveBalance?.[leaveType];
    
    // Safely get balance values
    const balance = (rawBalance && typeof rawBalance === 'object')
      ? {
          total: getBalanceValue(rawBalance, 'total'),
          used: getBalanceValue(rawBalance, 'used'),
          pending: getBalanceValue(rawBalance, 'pending')
        }
      : { total: 0, used: 0, pending: 0 };

    if (balance.total - balance.used - balance.pending < days) {
      toast.error('Insufficient leave balance');
      return;
    }

    try {
      // Update pending balance
      await db.leaveBalances.where('userId').equals(user.id).modify({
        [`${leaveType}.pending`]: balance.pending + days
      });

      // Get user's supervisor
      const userData = await db.users.get(user.id);
      const supervisorId = userData?.supervisorId || null;

      // Add leave request to database
      const leaveRequest = {
        id: uuidv4(),
        userId: user.id,
        leaveType: leaveType,
        startDate: new Date(newRequest.startDate).getTime(),
        endDate: new Date(newRequest.endDate).getTime(),
        days: days,
        reason: newRequest.reason || '',
        status: 'pending',
        supervisorId: supervisorId,
        approvedBy: null,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      
      await db.leaveRequests.add(leaveRequest);
      
      toast.success('Leave request submitted');
      setShowRequestModal(false);
      setNewRequest({ type: '', startDate: '', endDate: '', reason: '' });
      loadData();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error('Failed to submit request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-emerald-100 text-emerald-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'pending_ceo': return 'bg-purple-100 text-purple-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending_ceo': return 'Under CEO Review';
      case 'pending': return 'Pending';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const getBalanceValue = (balance, key) => {
    const val = balance?.[key];
    return typeof val === 'number' && !isNaN(val) ? val : 0;
  };

  const totalAvailable = leaveTypes.reduce((sum, type) => {
    const balance = leaveBalance?.[type.value];
    if (!balance || typeof balance !== 'object') return sum;
    const total = getBalanceValue(balance, 'total');
    const used = getBalanceValue(balance, 'used');
    const pending = getBalanceValue(balance, 'pending');
    return sum + (total - used - pending);
  }, 0);

  const totalUsed = leaveTypes.reduce((sum, type) => {
    const balance = leaveBalance?.[type.value];
    if (!balance || typeof balance !== 'object') return sum;
    return sum + getBalanceValue(balance, 'used');
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Balance</h1>
          <p className="text-gray-500 mt-1">Manage your time off and leave requests</p>
        </div>
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Request Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Request Leave</DialogTitle>
              <DialogDescription>
                Submit a new leave request for approval
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="leaveType">Leave Type *</Label>
                <Select 
                  value={newRequest.type} 
                  onValueChange={(value) => setNewRequest(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map(type => {
                      const rawBalance = leaveBalance?.[type.value];
                      const balance = (rawBalance && typeof rawBalance === 'object') 
                        ? {
                            total: getBalanceValue(rawBalance, 'total'),
                            used: getBalanceValue(rawBalance, 'used'),
                            pending: getBalanceValue(rawBalance, 'pending')
                          }
                        : { total: 0, used: 0, pending: 0 };
                      const available = balance.total - balance.used - balance.pending;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <type.icon className="w-4 h-4" />
                              {type.label}
                            </div>
                            <span className="text-gray-500 ml-4">({available} days)</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={newRequest.startDate}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, startDate: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">End Date *</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={newRequest.endDate}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, endDate: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {newRequest.startDate && newRequest.endDate && (
                <div className="p-3 bg-teal-50 rounded-lg text-center">
                  <p className="text-sm text-teal-700">
                    Duration: <span className="font-bold">{getDaysBetween(newRequest.startDate, newRequest.endDate) + 1} day(s)</span>
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="reason">Reason (Optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Reason for leave..."
                  value={newRequest.reason}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, reason: e.target.value }))}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
              <Button 
                onClick={handleSubmitRequest}
                className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
              >
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100">Available Balance</p>
                <p className="text-3xl font-bold mt-1">{totalAvailable} days</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Used This Year</p>
                <p className="text-2xl font-bold text-gray-900">{totalUsed} days</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{leaveRequests.filter(r => r.status === 'pending').length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Type Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {leaveTypes.map(type => {
          const rawBalance = leaveBalance?.[type.value];
          const balance = (rawBalance && typeof rawBalance === 'object') 
            ? {
                total: getBalanceValue(rawBalance, 'total'),
                used: getBalanceValue(rawBalance, 'used'),
                pending: getBalanceValue(rawBalance, 'pending')
              }
            : { total: 0, used: 0, pending: 0 };
          const available = balance.total - balance.used - balance.pending;
          const usedPercentage = balance.total > 0 ? (balance.used / balance.total) * 100 : 0;
          const pendingPercentage = balance.total > 0 ? (balance.pending / balance.total) * 100 : 0;
          
          return (
            <Card key={type.value} className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.color}`}>
                      <type.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{type.label}</p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Available</span>
                    <span className="font-bold text-gray-900">{available} / {balance.total} days</span>
                  </div>
                  
                  <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="absolute left-0 top-0 h-full bg-teal-500"
                      style={{ width: `${100 - usedPercentage - pendingPercentage}%` }}
                    />
                    <div 
                      className="absolute top-0 h-full bg-amber-400"
                      style={{ left: `${100 - usedPercentage - pendingPercentage}%`, width: `${pendingPercentage}%` }}
                    />
                  </div>

                  <div className="flex gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-gray-300 rounded-full" />
                      <span className="text-gray-500">Used: {balance.used}</span>
                    </div>
                    {balance.pending > 0 && (
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-amber-400 rounded-full" />
                        <span className="text-gray-500">Pending: {balance.pending}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Leave Requests */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Leave Requests</CardTitle>
          <CardDescription>Your recent leave request history</CardDescription>
        </CardHeader>
        <CardContent>
          {leaveRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p>No leave requests yet</p>
              <p className="text-sm text-gray-400 mt-1">Click "Request Leave" to submit a new request</p>
            </div>
          ) : (
            <div className="space-y-4">
              {leaveRequests.map(request => {
                const typeInfo = leaveTypes.find(t => t.value === request.type);
                return (
                  <div 
                    key={request.id}
                    className={`flex flex-col p-4 border rounded-xl ${request.status === 'rejected' ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${typeInfo?.color || 'bg-gray-100'}`}>
                          {typeInfo && <typeInfo.icon className="w-6 h-6" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900">{typeInfo?.label}</p>
                            <Badge className={getStatusColor(request.status)}>
                              {getStatusLabel(request.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">{request.days} day{request.days > 1 ? 's' : ''}</p>
                        <p className="text-xs text-gray-400">{formatDate(request.submittedAt)}</p>
                      </div>
                    </div>
                    {request.status === 'rejected' && request.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-red-700">Rejection Reason</p>
                            <p className="text-sm text-red-600 mt-1">{request.rejectionReason}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {request.status === 'rejected' && !request.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <p className="text-sm text-red-600">This request was rejected. No reason provided.</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Leave Policy Highlights</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>Annual leave can be carried forward up to 10 days</li>
                <li>Sick leave requires medical certificate for 3+ consecutive days</li>
                <li>Casual leave cannot exceed 3 consecutive days</li>
                <li>Leave requests should be submitted at least 3 days in advance</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
